import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  platformAutomationsApi,
  PlatformAutomation,
  PlatformAutomationActivityLogRow,
  PlatformAutomationEnrollmentRow,
  PlatformMessageTemplate,
} from "../../../cores/platformAutomations/services/api";
import {
  draftToJson,
  hydrateStepTemplatesForTest,
  hydrateDraftStepsForTest,
  normalizeDraft,
  parseDraftJson,
} from "../../../cores/platformAutomations/utils/draftDefinition.utils";
import {
  PlatformAutomationDraft,
  SendEmailStep,
} from "../../../cores/platformAutomations/types";
import { useRouterCore } from "../../../cores/router";
import { toast } from "react-toastify";
import PlatformAutomationAdvancedJson from "../../component/platformAutomations/PlatformAutomationAdvancedJson";
import PlatformAutomationSettingsPanel from "../../component/platformAutomations/PlatformAutomationSettingsPanel";
import PlatformAutomationStepsEditor from "../../component/platformAutomations/PlatformAutomationStepsEditor";
import PlatformAutomationTimeline from "../../component/platformAutomations/PlatformAutomationTimeline";
import PlatformAutomationTestPanel from "../../component/platformAutomations/PlatformAutomationTestPanel";
import PlatformAutomationEnrollmentsPanel from "../../component/platformAutomations/PlatformAutomationEnrollmentsPanel";
import PlatformAutomationActivityPanel from "../../component/platformAutomations/PlatformAutomationActivityPanel";
import Content from "../../layout/Content";
import Layout from "../../layout/Layout";
import "../emailMarketing/EmailMarketing.scss";
import "./PlatformAutomations.scss";

type EditorTab = "flow" | "enrollments" | "logs" | "test";

const STATUS_PILL: Record<string, string> = {
  published: "pa-status-pill pa-status-pill--published",
  draft: "pa-status-pill pa-status-pill--draft",
  paused: "pa-status-pill pa-status-pill--paused",
  archived: "pa-status-pill pa-status-pill--archived",
};

export default function PlatformAutomationEditor() {
  const { id } = useParams<{ id: string }>();
  const intl = useIntl();
  const { goToPlatformAutomations } = useRouterCore();
  const [automation, setAutomation] = useState<
    (PlatformAutomation & { versions?: unknown[] }) | null
  >(null);
  const [draft, setDraft] = useState<PlatformAutomationDraft>(normalizeDraft({}));
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<EditorTab>("flow");
  const [enrollments, setEnrollments] = useState<PlatformAutomationEnrollmentRow[]>(
    []
  );
  const [logs, setLogs] = useState<PlatformAutomationActivityLogRow[]>([]);
  const [activityFilterEnrollmentId, setActivityFilterEnrollmentId] = useState<
    number | null
  >(null);
  const [testEmail, setTestEmail] = useState("");
  const [testStepId, setTestStepId] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const [runningFlowTest, setRunningFlowTest] = useState(false);
  const [templates, setTemplates] = useState<PlatformMessageTemplate[]>([]);
  const [showAdvancedJson, setShowAdvancedJson] = useState(false);
  const [advancedJson, setAdvancedJson] = useState("");

  const loadActivityLogs = useCallback(
    async (automationId: number, enrollmentId?: number | null) => {
      const logRows = await platformAutomationsApi.listLogs(
        automationId,
        enrollmentId ?? undefined
      );
      setLogs(logRows);
    },
    []
  );

  const load = useCallback(async () => {
    if (!id) return;
    const automationId = Number(id);
    const [data, enrollmentRows, logRows, templateRows] = await Promise.all([
      platformAutomationsApi.get(automationId),
      platformAutomationsApi.listEnrollments(automationId),
      platformAutomationsApi.listLogs(automationId),
      platformAutomationsApi.listTemplates(),
    ]);
    const normalized = normalizeDraft(data.draftDefinition);
    setAutomation(data);
    setName(data.name);
    setDraft(normalized);
    setAdvancedJson(draftToJson(normalized));
    setEnrollments(enrollmentRows);
    setLogs(logRows);
    setTemplates(templateRows);
  }, [id]);

  const viewEnrollmentActivity = useCallback(
    async (enrollmentId: number) => {
      if (!id) return;
      setActivityFilterEnrollmentId(enrollmentId);
      setActiveTab("logs");
      await loadActivityLogs(Number(id), enrollmentId);
    },
    [id, loadActivityLogs]
  );

  const handleActivityFilterChange = useCallback(
    async (enrollmentId: number | null) => {
      if (!id) return;
      setActivityFilterEnrollmentId(enrollmentId);
      await loadActivityLogs(Number(id), enrollmentId);
    },
    [id, loadActivityLogs]
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setAdvancedJson(draftToJson(draft));
  }, [draft]);

  const emailSteps = useMemo(
    () => (draft.steps || []).filter((s) => s.type === "send_email"),
    [draft.steps]
  );

  const delaySteps = useMemo(
    () => (draft.steps || []).filter((s) => s.type === "delay"),
    [draft.steps]
  );

  const activeEnrollments = useMemo(
    () => enrollments.filter((row) => row.status === "active").length,
    [enrollments]
  );

  useEffect(() => {
    if (!emailSteps.length) {
      setTestStepId("");
      return;
    }
    if (!emailSteps.some((step) => step.id === testStepId)) {
      setTestStepId(emailSteps[0].id);
    }
  }, [emailSteps, testStepId]);

  const statusLabel = (status: string) =>
    intl.formatMessage({
      id: `app.platformAutomations.status.${status}`,
      defaultMessage: status,
    });

  const saveDraft = async (silent = false): Promise<boolean> => {
    if (!id) return false;
    setSaving(true);
    try {
      const updated = await platformAutomationsApi.updateDraft(Number(id), {
        name,
        draftDefinition: draft as Record<string, unknown>,
      });
      setAutomation(updated);
      if (!silent) {
        toast.success(
          intl.formatMessage({ id: "app.platformAutomations.editor.saved" })
        );
      }
      return true;
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.platformAutomations.editor.saveError" })
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const applyAdvancedJson = () => {
    try {
      setDraft(parseDraftJson(advancedJson));
      toast.success(
        intl.formatMessage({ id: "app.platformAutomations.editor.jsonApplied" })
      );
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.platformAutomations.editor.jsonInvalid" })
      );
    }
  };

  const formatAdvancedJson = () => {
    try {
      setAdvancedJson(JSON.stringify(JSON.parse(advancedJson), null, 2));
      toast.success(
        intl.formatMessage({ id: "app.platformAutomations.editor.jsonFormatted" })
      );
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.platformAutomations.editor.jsonInvalid" })
      );
    }
  };

  const copyAdvancedJson = async () => {
    try {
      await navigator.clipboard.writeText(advancedJson);
      toast.success(
        intl.formatMessage({ id: "app.platformAutomations.editor.jsonCopied" })
      );
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.platformAutomations.editor.jsonCopyError" })
      );
    }
  };

  const resetAdvancedJson = () => {
    setAdvancedJson(draftToJson(draft));
    toast.info(
      intl.formatMessage({ id: "app.platformAutomations.editor.jsonReset" })
    );
  };

  const publish = async () => {
    if (!id) return;
    try {
      const saved = await saveDraft(true);
      if (!saved) return;
      await platformAutomationsApi.publish(Number(id));
      await load();
      toast.success(
        intl.formatMessage({ id: "app.platformAutomations.editor.published" })
      );
    } catch (error: unknown) {
      const apiMessage = (
        error as {
          response?: { data?: { message?: string | { message?: string } } };
        }
      )?.response?.data?.message;
      const detail =
        typeof apiMessage === "string" ? apiMessage : apiMessage?.message;
      toast.error(
        detail ||
          intl.formatMessage({ id: "app.platformAutomations.editor.publishError" })
      );
    }
  };

  const pause = async () => {
    if (!id) return;
    try {
      await platformAutomationsApi.pause(Number(id));
      await load();
      toast.success(
        intl.formatMessage({ id: "app.platformAutomations.editor.paused" })
      );
    } catch {
      toast.error(
        intl.formatMessage({ id: "app.platformAutomations.editor.pauseError" })
      );
    }
  };

  const openTestStep = (stepId: string) => {
    setTestStepId(stepId);
    setActiveTab("test");
  };

  const sendTestFlow = async () => {
    if (!id) return;
    setRunningFlowTest(true);
    try {
      const saved = await saveDraft(true);
      if (!saved) return;
      const snapshot = {
        ...(draft as Record<string, unknown>),
        steps: hydrateDraftStepsForTest(draft.steps || [], templates),
      };
      const result = await platformAutomationsApi.testFlow(Number(id), {
        snapshot,
        targetEmail: testEmail || undefined,
      });
      await load();
      const scheduleLabel = result.firstScheduleAt
        ? new Date(result.firstScheduleAt).toLocaleString("es-CL")
        : intl.formatMessage({
            id: "app.platformAutomations.editor.testFlowImmediate",
          });
      toast.success(
        intl.formatMessage(
          { id: "app.platformAutomations.editor.testFlowStarted" },
          { email: result.recipient, schedule: scheduleLabel }
        )
      );
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ||
        intl.formatMessage({ id: "app.platformAutomations.editor.testFlowError" });
      toast.error(message);
    } finally {
      setRunningFlowTest(false);
    }
  };

  const sendTestEmail = async (stepId = testStepId) => {
    if (!id || !stepId) return;
    const rawStep = (draft.steps || []).find(
      (item) => item.id === stepId && item.type === "send_email"
    );
    if (!rawStep) {
      toast.error(
        intl.formatMessage({ id: "app.platformAutomations.editor.testInvalidStep" })
      );
      return;
    }
    const step = hydrateStepTemplatesForTest(rawStep, templates) as SendEmailStep;
    setSendingTest(true);
    try {
      await platformAutomationsApi.testEmail({
        step: step as Record<string, unknown>,
        snapshot: draft as Record<string, unknown>,
        targetEmail: testEmail || undefined,
      });
      toast.success(
        intl.formatMessage({ id: "app.platformAutomations.editor.testSent" })
      );
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ||
        intl.formatMessage({ id: "app.platformAutomations.editor.testError" });
      toast.error(message);
    } finally {
      setSendingTest(false);
    }
  };

  const tabs: Array<{ id: EditorTab; label: React.ReactNode }> = [
    {
      id: "flow",
      label: <FormattedMessage id="app.platformAutomations.editor.tabFlow" />,
    },
    {
      id: "enrollments",
      label: (
        <FormattedMessage
          id="app.platformAutomations.editor.tabUsers"
          values={{ count: enrollments.length }}
        />
      ),
    },
    {
      id: "logs",
      label: (
        <FormattedMessage
          id="app.platformAutomations.editor.tabActivity"
          values={{ count: logs.length }}
        />
      ),
    },
    {
      id: "test",
      label: <FormattedMessage id="app.platformAutomations.editor.tabTest" />,
    },
  ];

  if (!automation) {
    return (
      <Layout>
        <Content className="email-mkt pa-editor">
          <p className="email-mkt__note">
            <FormattedMessage id="app.platformAutomations.editor.loading" />
          </p>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout>
      <Content className="email-mkt pa-editor">
        <section className="email-mkt__hero pa-editor-hero">
          <div className="pa-editor-hero__top">
            <button
              type="button"
              className="email-mkt__ghost-btn pa-editor-hero__back"
              onClick={goToPlatformAutomations}
            >
              <FormattedMessage id="app.platformAutomations.editor.back" />
            </button>
            <div className="pa-editor-hero__actions">
              <button
                type="button"
                className="email-mkt__ghost-btn"
                disabled={saving}
                onClick={() => saveDraft()}
              >
                {saving ? (
                  <FormattedMessage id="app.platformAutomations.editor.saving" />
                ) : (
                  <FormattedMessage id="app.platformAutomations.editor.save" />
                )}
              </button>
              <button
                type="button"
                className="email-mkt__primary-btn"
                onClick={publish}
              >
                <FormattedMessage id="app.platformAutomations.editor.publish" />
              </button>
              {automation.status === "published" && (
                <button
                  type="button"
                  className="email-mkt__ghost-btn pa-editor-hero__pause"
                  onClick={pause}
                >
                  <FormattedMessage id="app.platformAutomations.editor.pause" />
                </button>
              )}
            </div>
          </div>

          <div className="email-mkt__hero-content pa-editor-hero__content">
            <div className="pa-editor-hero__copy">
              <span className="email-mkt__eyebrow">
                <FormattedMessage id="app.platformAutomations.templatesLibrary.eyebrow" />
              </span>
              <label className="pa-editor-hero__name-field">
                <span className="pa-editor-hero__name-label">
                  <FormattedMessage id="app.platformAutomations.editor.flowName" />
                </span>
                <input
                  className="pa-editor-hero__name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={intl.formatMessage({
                    id: "app.platformAutomations.editor.flowNamePlaceholder",
                  })}
                />
              </label>
              <div className="pa-editor-hero__meta">
                <span
                  className={
                    STATUS_PILL[automation.status] || "pa-status-pill"
                  }
                >
                  {statusLabel(automation.status)}
                </span>
                {automation.publishedVersionId ? (
                  <span className="pa-editor-hero__version">
                    <FormattedMessage
                      id="app.platformAutomations.editor.publishedVersion"
                      values={{ version: automation.publishedVersionId }}
                    />
                  </span>
                ) : null}
              </div>
            </div>

            <div className="email-mkt__stats">
              <div className="email-mkt__stat">
                <strong>{draft.steps?.length ?? 0}</strong>
                <span>
                  <FormattedMessage id="app.platformAutomations.editor.statSteps" />
                </span>
              </div>
              <div className="email-mkt__stat">
                <strong>{emailSteps.length}</strong>
                <span>
                  <FormattedMessage id="app.platformAutomations.editor.statEmails" />
                </span>
              </div>
              <div className="email-mkt__stat">
                <strong>{delaySteps.length}</strong>
                <span>
                  <FormattedMessage id="app.platformAutomations.editor.statWaits" />
                </span>
              </div>
              <div className="email-mkt__stat">
                <strong>{activeEnrollments}</strong>
                <span>
                  <FormattedMessage id="app.platformAutomations.editor.statActive" />
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="email-mkt__panel pa-editor-panel">
          <div className="email-mkt__toolbar email-mkt__toolbar--split pa-editor-tabs">
            <div className="email-mkt__segmented">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={activeTab === tab.id ? "is-active" : ""}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {activeTab === "flow" && (
            <div className="pa-editor-flow">
              {(draft.steps?.length ?? 0) > 0 && (
                <PlatformAutomationTimeline draft={draft} templates={templates} />
              )}

              <div className="pa-editor-grid">
                <PlatformAutomationSettingsPanel
                  draft={draft}
                  onChange={setDraft}
                />
                <div className="pa-editor-main">
                  <PlatformAutomationStepsEditor
                    draft={draft}
                    templates={templates}
                    onChange={setDraft}
                    onTestStep={openTestStep}
                  />
                </div>
              </div>

              <PlatformAutomationAdvancedJson
                open={showAdvancedJson}
                onToggle={() => setShowAdvancedJson((open) => !open)}
                value={advancedJson}
                onChange={setAdvancedJson}
                onApply={applyAdvancedJson}
                onReset={resetAdvancedJson}
                onFormat={formatAdvancedJson}
                onCopy={copyAdvancedJson}
              />
            </div>
          )}

          {activeTab === "enrollments" && (
            <div className="pa-data-panel">
              {enrollments.length === 0 ? (
                <div className="pa-empty-state">
                  <FontAwesomeIcon icon={["fas", "users"]} />
                  <p>
                    <FormattedMessage id="app.platformAutomations.editor.enrollmentsEmpty" />
                  </p>
                </div>
              ) : (
                <PlatformAutomationEnrollmentsPanel
                  enrollments={enrollments}
                  onViewActivity={viewEnrollmentActivity}
                  onStop={async (enrollmentId) => {
                    await platformAutomationsApi.stopEnrollment(enrollmentId);
                    await load();
                  }}
                />
              )}
            </div>
          )}

          {activeTab === "logs" && (
            <div className="pa-data-panel">
              <PlatformAutomationActivityPanel
                logs={logs}
                enrollments={enrollments}
                filterEnrollmentId={activityFilterEnrollmentId}
                onFilterChange={handleActivityFilterChange}
              />
            </div>
          )}

          {activeTab === "test" && (
            <PlatformAutomationTestPanel
              draft={draft}
              templates={templates}
              selectedStepId={testStepId}
              onSelectStep={setTestStepId}
              targetEmail={testEmail}
              onTargetEmailChange={setTestEmail}
                    onSend={sendTestEmail}
                    onRunFlow={sendTestFlow}
                    sending={sendingTest}
                    runningFlow={runningFlowTest}
            />
          )}
        </section>
      </Content>
    </Layout>
  );
}
